import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import hearts from '../Assets/landing/hearts.png'
import stars from '../Assets/landing/stars.png'
import ChevronDown from '../Assets/landing/Chevron_Down.png'
import group from '../Assets/landing/bottom-hero.webp'
import featureGuestRsvp from '../Assets/landing/features/rsvp.png'
import featureWebsite from '../Assets/landing/features/website.png'
import featureTablePlan from '../Assets/landing/features/table-plan.webp'
import featureSchedule from '../Assets/landing/features/schedule.png'
import featureChecklist from '../Assets/landing/features/todo-list.png'
import featureGuestGallery from '../Assets/landing/features/guest-gallery.webp'
import featureSaveTheDate from '../Assets/landing/features/save-the-date.png'
import featureCouplePlanning from '../Assets/landing/features/couple-planning.webp'
import featureArrowLeft from '../Assets/landing/features/arrow-left.png'
import featureArrowRight from '../Assets/landing/features/arrow-right.png'
import './AppLandingPage.css'
import MarketingLayout from './marketing/MarketingLayout'
import Seo from './marketing/Seo'
import StoreBadges from './marketing/StoreBadges'

export default function AppLandingPage() {
  const location = useLocation()

  const [openFaqIndex, setOpenFaqIndex] = useState(null)
  const [scrollX, setScrollX] = useState(0)

  // Support /#faq and /#features links from other pages
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1))
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [location.hash])

  useEffect(() => {
    const handleScroll = () => {
      setScrollX(window.scrollX || window.pageXOffset || 0)
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const toggleFaq = (index) => {
    setOpenFaqIndex((prev) => (prev === index ? null : index))
  }

  const featureSlides = [
    {
      id: 'guest-rsvp',
      title: 'Guest list & RSVP tracking',
      mobileLines: ['Guest list &', 'RSVP tracking'],
      description:
        'Add guests once, track plus-ones and collect RSVPs in one secure place. Quickly see who’s attending your ceremony and evening reception.',
      cta: 'Learn more about RSVPs',
      link: '/features/rsvp',
      image: featureGuestRsvp,
    },
    {
      id: 'website',
      title: 'Wedding website builder',
      mobileLines: ['Wedding website', 'builder'],
      description:
        'Create a simple UK wedding website with your story, venue details and RSVP form. Share a single link with your guests – no coding required.',
      cta: 'Learn more about wedding websites',
      link: '/features/wedding-website',
      image: featureWebsite,
    },
    {
      id: 'table-plan',
      title: 'Table plan & seating plan',
      mobileLines: ['Table plan &', 'seating plan'],
      description:
        'Build your table plan and seating layout directly from your guest list, so every place setting has a confirmed name and RSVP.',
      cta: 'How seating plans work',
      link: '/features/table-planner',
      image: featureTablePlan,
    },
    {
      id: 'schedule',
      title: 'Wedding day timeline & schedule',
      mobileLines: ['Wedding day timeline', '& schedule'],
      description:
        'Plan your full schedule of the day – from prep to first dance – and keep the timings handy for your venue and suppliers.',
      cta: 'Schedule of the day',
      link: '/features/day-schedule',
      image: featureSchedule,
    },
    {
      id: 'checklist',
      title: 'Checklist & planning progress',
      mobileLines: ['Checklist &', 'planning progress'],
      description:
        'Use a guided checklist based on a typical UK wedding timeline so you know what to do and when, from engagement to the week before.',
      cta: 'When to start planning',
      link: '/features/checklist',
      image: featureChecklist,
    },
    {
      id: 'partner-planning',
      title: 'Partner planning',
      description:
        'Planning together? Log in from both of your phones so you stay on the same page. Partner planning tools are being expanded soon.',
      cta: 'Explore all features',
      link: '/features',
      image: featureCouplePlanning,
    },
    {
      id: 'guest-photos',
      title: 'Guest upload gallery',
      description:
        'Make it easy for guests to upload photos from the big day into one shared space, ideal for candid moments you might otherwise miss.',
      cta: 'Guest photo uploads',
      link: '/features/guest-photos',
      image: featureGuestGallery,
    },
    {
      id: 'save-the-date',
      title: 'Save the date & schedule sharing',
      mobileLines: ['Save the date &', 'schedule sharing'],
      description:
        'Share your save-the-date and schedule links directly from the app so guests always know where to be and when.',
      cta: 'Digital save the dates',
      link: '/features/save-the-date',
      image: featureSaveTheDate,
    },
  ]

  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0)
  const featureTrackRef = useRef(null)
  const confettiContainerRef = useRef(null)

  const scrollToFeature = (index) => {
    if (!featureTrackRef.current) return
    const track = featureTrackRef.current
    const slides = track.children
    const target = slides[index]
    if (target && target.scrollIntoView) {
      target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
    }
  }

  const goToNextFeature = () => {
    const next = (activeFeatureIndex + 1) % featureSlides.length
    scrollToFeature(next)
  }

  const goToPrevFeature = () => {
    const prevIndex =
      activeFeatureIndex === 0 ? featureSlides.length - 1 : activeFeatureIndex - 1
    scrollToFeature(prevIndex)
  }

  // Update active index when user scrolls the feature track manually
  useEffect(() => {
    if (!featureTrackRef.current) return
    const track = featureTrackRef.current

    const handleScroll = () => {
      const slides = Array.from(track.children)
      if (!slides.length) return

      const trackRect = track.getBoundingClientRect()
      const trackCenter = trackRect.left + trackRect.width / 2

      let closestIndex = 0
      let closestDistance = Infinity

      slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect()
        const slideCenter = rect.left + rect.width / 2
        const distance = Math.abs(slideCenter - trackCenter)
        if (distance < closestDistance) {
          closestDistance = distance
          closestIndex = index
        }
      })

      setActiveFeatureIndex(closestIndex)
    }

    track.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      track.removeEventListener('scroll', handleScroll)
    }
  }, [featureSlides.length])

  const confettiPieces = useMemo(() => {
    const total = 42
    const shades = [
      'rgba(244, 183, 181, 0.75)',
      'rgba(244, 183, 181, 0.6)',
      'rgba(255, 197, 199, 0.7)',
      'rgba(255, 214, 220, 0.7)',
      'rgba(255, 240, 243, 0.7)',
    ]

    return Array.from({ length: total }, (_, i) => {
      const seed = i * 137.5
      const x = (seed * 7.13) % 100
      const delay = -((seed * 0.11) % 9)
      const duration = 11 + ((seed * 0.07) % 8)
      const drift = -18 + ((seed * 0.23) % 36)
      const size = 5 + ((seed * 0.17) % 8)
      const topOffset = (seed * 0.19) % 100
      const rotation = (seed * 0.41) % 360
      const opacity = 0.35 + (((seed * 0.09) % 50) / 100)

      return {
        id: i,
        x,
        delay,
        duration,
        drift,
        size,
        topOffset,
        rotation,
        opacity,
        color: shades[i % shades.length],
      }
    })
  }, [])

  const appJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: 'First Dance',
    operatingSystem: 'iOS, Android',
    applicationCategory: 'LifestyleApplication',
    description:
      'First Dance is a UK wedding planning app for guest lists, RSVPs, wedding websites, seating plans, schedules and checklists. The fun way to plan your wedding day.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'GBP',
    },
    url: 'https://firstdance.app',
    sameAs: [
      'https://www.facebook.com/firstdanceapp',
      'https://www.instagram.com/firstdanceapp/',
    ],
  }

  return (
    <MarketingLayout>
      <Seo
        title="First Dance | Wedding Planner App UK"
        description="The fun way to plan your wedding day. Manage your guest list, RSVPs, wedding website, seating plan, schedule and checklist – all from your phone, free."
        path="/"
      />
      <script type="application/ld+json">{JSON.stringify(appJsonLd)}</script>

      <div className='app-download-container hero-download-container'>
        <div className="hero-content-inner" id="about">
          <div className='app-download-info'>
            <h1 id='app-header-txt'>
              The fun way to plan your wedding day
            </h1>
            <p className="hero-intro" style={{ color: '#454C5C', fontSize: 18, fontWeight: 500, margin: '28px 0 32px' }}>
              First Dance is a UK-based wedding planning app that helps you manage your guest list, RSVPs,
              wedding website, seating plan, schedule of the day and planning checklist – all from your phone.
              Plan together with your partner, stay organised and keep everything for your big day in one place.
            </p>
            <p style={{ color: '#FF8587', fontSize: 14, fontWeight: 500, margin: '28px 0' }}>
              Explore the{' '}
              <Link to="/features" style={{ color: '#FF8587', textDecoration: 'underline' }}>
                wedding planner app features
              </Link>
              , check our{' '}
              <Link to="/pricing" style={{ color: '#FF8587', textDecoration: 'underline' }}>
                simple pricing
              </Link>{' '}
              or jump down to our{' '}
              <a href="#faq" style={{ color: '#FF8587', textDecoration: 'underline' }}>
                wedding planning app FAQ
              </a>
              . For more ideas, see our{' '}
              <Link to="/blog" style={{ color: '#FF8587', textDecoration: 'underline' }}>
                wedding planning tips
              </Link>
              .
            </p>
            <StoreBadges style={{ marginTop: 24 }} />
          </div>
        </div>
      </div>

      <div className="hero-divider">
        <div className="hero-divider-inner">
          <img
            src={hearts}
            alt="Decorative hearts"
            className="hero-hearts"
          />
          <img
            src={stars}
            alt="Decorative stars"
            className="hero-stars"
          />
        </div>
      </div>

      <section id="features" className="features-section">
        {/* Soft falling confetti background just for features */}
        <div
          ref={confettiContainerRef}
          className="confetti-layer"
          aria-hidden="true"
        >
          <div
            className="confetti-parallax"
            style={{ transform: `translateX(${-scrollX * 0.18}px)` }}
          >
            {confettiPieces.map((piece) => (
              <span
                key={piece.id}
                className="confetti-piece"
                style={{
                  left: `${piece.x}%`,
                  top: `${piece.topOffset}%`,
                  width: `${piece.size}px`,
                  height: `${Math.max(piece.size * 1.4, 8)}px`,
                  opacity: piece.opacity,
                  backgroundColor: piece.color,
                  filter: 'blur(0.2px)',
                  animationDuration: `${piece.duration}s`,
                  animationDelay: `${piece.delay}s`,
                  '--drift-start': `${piece.drift * 0.35}px`,
                  '--drift-end': `${piece.drift}px`,
                  '--rotate-start': `${piece.rotation}deg`,
                }}
              />
            ))}
          </div>
        </div>

        <div id='why-download'>
          <h2>All-in-one wedding planner app features</h2>
          <p style={{ maxWidth: 900, margin: '0 auto 2rem' }}>
            First Dance brings together the core tools UK couples actually need – guest list, RSVPs, wedding
            website, seating plan, schedule and checklist – into one simple app. No more juggling multiple apps
            or spreadsheets. Dive deeper on our{' '}
            <Link to="/features" style={{ color: '#FF8587', textDecoration: 'underline' }}>
              features pages
            </Link>
            .
          </p>

          <div className="feature-slider">
            <button
              type="button"
              className="feature-slider-arrow feature-slider-arrow-left"
              onClick={goToPrevFeature}
              aria-label="Previous feature"
            >
              <img src={featureArrowLeft} alt="" className="feature-slider-arrow-icon" />
            </button>

            <div className="feature-slider-track" ref={featureTrackRef}>
              {featureSlides.map((slide) => (
                <div className="feature-slide" key={slide.id}>
                  <div className="feature-slide-image-wrap">
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="feature-slide-image"
                    />
                  </div>
                  <div className="feature-slide-content">
                    <h3 className="feature-title-desktop">{slide.title}</h3>
                    <div className="feature-title-mobile">
                      {(slide.mobileLines || [slide.title]).map((line) => (
                        <div key={line} className="feature-title-chip">
                          {line}
                        </div>
                      ))}
                    </div>
                    <p>{slide.description}</p>
                    <Link to={slide.link} className="feature-learn-more">
                      {slide.cta}
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="feature-slider-arrow feature-slider-arrow-right"
              onClick={goToNextFeature}
              aria-label="Next feature"
            >
              <img src={featureArrowRight} alt="" className="feature-slider-arrow-icon" />
            </button>
          </div>

          <div className="feature-slider-dots">
            {featureSlides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                className={
                  index === activeFeatureIndex
                    ? 'feature-dot feature-dot-active'
                    : 'feature-dot'
                }
                onClick={() => scrollToFeature(index)}
                aria-label={`Go to feature: ${slide.title}`}
              />
            ))}
          </div>
        </div>
      </section>

      <div className="stripes-divider" />

      {/* FAQ section */}
      <section id="faq" style={{ backgroundColor: '#FBFAF9' }}>
        <h2
          style={{
            textAlign: 'center',
            margin: '0 auto 1rem',
            fontWeight: 900,
            maxWidth: '95vw',
          }}
        >
          Wedding planning app FAQ (UK)
        </h2>
        <p
          style={{
            maxWidth: 600,
            width: '90vw',
            margin: '0 auto 2.5rem',
            textAlign: 'center',
          }}
        >
          Everything you need to know about using the app and planning your wedding. Browse the questions
          below to find the answers you’re looking for.
        </p>
        <div className="faq-section">
          <div
            className={openFaqIndex === 0 ? 'faq-item faq-item-open' : 'faq-item'}
            onClick={() => toggleFaq(0)}
          >
            <div className="faq-item-header">
              <h3>What is the best wedding planning app in the UK?</h3>
              <img
                src={ChevronDown}
                alt=""
                className={`faq-chevron ${openFaqIndex === 0 ? 'open' : ''}`}
              />
            </div>
            <p className={openFaqIndex === 0 ? 'faq-answer-open' : 'faq-answer-closed'}>
              The best wedding planning app is the one that keeps everything in one place and feels easy
              to use. First Dance is designed specifically for UK couples, combining guest list, RSVPs,
              wedding website, seating plan, schedule and checklist in a single app.
            </p>
          </div>

          <div
            className={openFaqIndex === 1 ? 'faq-item faq-item-open' : 'faq-item'}
            onClick={() => toggleFaq(1)}
          >
            <div className="faq-item-header">
              <h3>Can I create a wedding website and collect RSVPs?</h3>
              <img
                src={ChevronDown}
                alt=""
                className={`faq-chevron ${openFaqIndex === 1 ? 'open' : ''}`}
              />
            </div>
            <p className={openFaqIndex === 1 ? 'faq-answer-open' : 'faq-answer-closed'}>
              Yes. With First Dance you can create a simple{' '}
              <Link to="/features/wedding-website">wedding website</Link> and share one link with your
              guests. They can <Link to="/features/rsvp">RSVP online</Link> and their responses flow
              straight into your guest list and seating plan. Once you’re set up, download the app and try
              it out for free.
            </p>
          </div>

          <div
            className={openFaqIndex === 2 ? 'faq-item faq-item-open' : 'faq-item'}
            onClick={() => toggleFaq(2)}
          >
            <div className="faq-item-header">
              <h3>How does the guest list and seating plan work?</h3>
              <img
                src={ChevronDown}
                alt=""
                className={`faq-chevron ${openFaqIndex === 2 ? 'open' : ''}`}
              />
            </div>
            <p className={openFaqIndex === 2 ? 'faq-answer-open' : 'faq-answer-closed'}>
              You add guests once, then use that list to manage RSVPs and build your{' '}
              <Link to="/features/table-planner">table plan</Link>. As people reply, you’ll always know
              who is seated where – ideal for coordinating with your venue and caterers.
            </p>
          </div>

          <div
            className={openFaqIndex === 3 ? 'faq-item faq-item-open' : 'faq-item'}
            onClick={() => toggleFaq(3)}
          >
            <div className="faq-item-header">
              <h3>Is First Dance free to use?</h3>
              <img
                src={ChevronDown}
                alt=""
                className={`faq-chevron ${openFaqIndex === 3 ? 'open' : ''}`}
              />
            </div>
            <p className={openFaqIndex === 3 ? 'faq-answer-open' : 'faq-answer-closed'}>
              First Dance offers a free tier so you can start planning your wedding without commitment.
              Some advanced features are part of our premium plans, but core tools like the guest list,
              RSVPs and schedule are available to get you started. See our{' '}
              <Link to="/pricing">pricing page</Link> for a full comparison.
            </p>
          </div>

          <div
            className={openFaqIndex === 4 ? 'faq-item faq-item-open' : 'faq-item'}
            onClick={() => toggleFaq(4)}
          >
            <div className="faq-item-header">
              <h3>When should I start planning after getting engaged?</h3>
              <img
                src={ChevronDown}
                alt=""
                className={`faq-chevron ${openFaqIndex === 4 ? 'open' : ''}`}
              />
            </div>
            <p className={openFaqIndex === 4 ? 'faq-answer-open' : 'faq-answer-closed'}>
              Many UK couples start planning 12–18 months before the wedding date, but there’s no single
              “right” answer. Our built-in checklist helps you see what to focus on next, whether you’ve
              got two years or six months to go. For more guidance, visit our{' '}
              <Link to="/blog">wedding planning tips</Link>.
            </p>
          </div>

          <div
            className={openFaqIndex === 5 ? 'faq-item faq-item-open' : 'faq-item'}
            onClick={() => toggleFaq(5)}
          >
            <div className="faq-item-header">
              <h3>Can my partner help plan in the app?</h3>
              <img
                src={ChevronDown}
                alt=""
                className={`faq-chevron ${openFaqIndex === 5 ? 'open' : ''}`}
              />
            </div>
            <p className={openFaqIndex === 5 ? 'faq-answer-open' : 'faq-answer-closed'}>
              Yes, First Dance is built for couples. You can both access shared information so guest
              numbers, timings and details stay aligned. Partner planning tools are being expanded, but you
              can already log in from both phones.
            </p>
          </div>

          <div
            className={openFaqIndex === 6 ? 'faq-item faq-item-open' : 'faq-item'}
            onClick={() => toggleFaq(6)}
          >
            <div className="faq-item-header">
              <h3>How do guests upload photos on the day?</h3>
              <img
                src={ChevronDown}
                alt=""
                className={`faq-chevron ${openFaqIndex === 6 ? 'open' : ''}`}
              />
            </div>
            <p className={openFaqIndex === 6 ? 'faq-answer-open' : 'faq-answer-closed'}>
              Guests can upload photos from your wedding day straight through your wedding website into
              one shared <Link to="/features/guest-photos">guest photo album</Link>. It’s a simple way to
              collect candid photos without relying on social media.
            </p>
          </div>

          <div
            className={openFaqIndex === 7 ? 'faq-item faq-item-open' : 'faq-item'}
            onClick={() => toggleFaq(7)}
          >
            <div className="faq-item-header">
              <h3>Where can I read your Terms &amp; Conditions?</h3>
              <img
                src={ChevronDown}
                alt=""
                className={`faq-chevron ${openFaqIndex === 7 ? 'open' : ''}`}
              />
            </div>
            <p className={openFaqIndex === 7 ? 'faq-answer-open' : 'faq-answer-closed'}>
              You can review our full{' '}
              <Link to="/terms">Terms &amp; Conditions</Link> any time. We recommend doing this before
              upgrading to a paid plan or inviting guests.
            </p>
          </div>
        </div>
        <div className="waves-divider" />
      </section>

      <div className='app-download-container bottom-cta' style={{ justifyContent: 'center' }}>
        <div className='app-download-info'>
          <p style={{ color: '#7B9EEB', fontWeight: 900, fontSize: 24, margin: '0 auto', maxWidth: '95%' }}>Your dream wedding starts here.</p>
          <p style={{ color: '#7B9EEB', fontWeight: 900, fontSize: 24, marginTop: '0 auto', maxWidth: '95%' }}>Download the app today!</p>
          <StoreBadges />
        </div>
        <img className='app-images' src={group} alt='UK couple using the First Dance wedding planning app together' />
      </div>
    </MarketingLayout>
  )
}
